/**
 * The registry's view state lives in the query string (`/registry?q=tanz&sort=name&view=table`),
 * so a search can be linked and reloaded. Updates replace the history entry rather than adding
 * one per keystroke. Defaults are left out of the URL.
 */
import type { SharkSort } from "@/api/types";

export type RegistryView = "cards" | "table";

export interface RegistryState {
  q: string;
  sort: SharkSort;
  view: RegistryView;
}

export const SORTS: readonly { value: SharkSort; label: string; hint: string }[] = [
  { value: "encounters", label: "Sightings", hint: "Most sightings first" },
  { value: "rarity", label: "Rarity", hint: "Fewest sightings first" },
  { value: "recent", label: "Recent", hint: "Most recently seen first" },
  { value: "name", label: "Name", hint: "A to Z" },
];

export const VIEWS: readonly { value: RegistryView; label: string }[] = [
  { value: "cards", label: "Cards" },
  { value: "table", label: "Table" },
];

export const DEFAULT_STATE: RegistryState = { q: "", sort: "encounters", view: "cards" };

function oneOf<T extends string>(value: string | null, options: readonly { value: T }[]) {
  return options.find((o) => o.value === value)?.value;
}

/** Read the state from a query string; unknown values fall back to the defaults. */
export function parseRegistryState(params: URLSearchParams): RegistryState {
  return {
    q: (params.get("q") ?? "").trim(),
    sort: oneOf(params.get("sort"), SORTS) ?? DEFAULT_STATE.sort,
    view: oneOf(params.get("view"), VIEWS) ?? DEFAULT_STATE.view,
  };
}

/**
 * Write `patch` over `current` into a new query string, dropping defaults and blank searches.
 * Parameters the registry does not own are kept.
 */
export function withRegistryState(
  current: URLSearchParams,
  patch: Partial<RegistryState>,
): URLSearchParams {
  const next = new URLSearchParams(current);
  const state = { ...parseRegistryState(current), ...patch };
  const values: Record<keyof RegistryState, string> = {
    q: state.q.trim(),
    sort: state.sort,
    view: state.view,
  };
  for (const key of Object.keys(values) as (keyof RegistryState)[]) {
    if (values[key] === DEFAULT_STATE[key]) next.delete(key);
    else next.set(key, values[key]);
  }
  return next;
}
